import React, { ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { Vehicle, RouteEntity } from '../types';
import { ROLES } from '../lib/roles';

const SIDEBAR_COLLAPSED_KEY = 'otimizarota:sidebar-collapsed';

function getInitialSidebarOpen() {
  // Every page renders its own <Layout>, so this component remounts on each
  // navigation — read the persisted desktop collapse preference so it survives that.
  if (!window.matchMedia('(min-width: 769px)').matches) return false;
  return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
}

function getBreadcrumb(pathname: string) {
  if (pathname.startsWith('/rotas/nova')) return 'Nova rota';
  if (pathname.startsWith('/rotas/')) return 'Detalhes da rota';
  if (pathname.startsWith('/rotas')) return 'Rotas';
  if (pathname.startsWith('/depositos')) return 'Depósitos';
  if (pathname.startsWith('/frota/nova') || pathname.startsWith('/frota')) return 'Otimizar frota (VRP)';
  if (pathname.startsWith('/veiculos')) return 'Veículos';
  if (pathname.startsWith('/lancamentos')) return 'Lançamentos operacionais';
  if (pathname.startsWith('/relatorios')) return 'Relatórios operacionais';
  if (pathname.startsWith('/custos') || pathname.startsWith('/bi')) return 'Custos & Rentabilidade (BI)';
  if (pathname.startsWith('/whatsapp')) return 'Rastreamento & WhatsApp';
  if (pathname.startsWith('/configuracoes')) return 'Configurações do sistema';
  return 'Dashboard';
}

export default function Layout({ children }: { children: ReactNode }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  const { logout, me, hasRole, isSuperAdmin, isImpersonating, login } = useAuth();
  // SUPER_ADMIN não gerencia usuários pela tela da empresa (ele não pertence
  // a uma empresa cliente); a gestão dele fica no Painel do SaaS.
  const canManageUsers = me?.role === ROLES.ADMIN;
  const canSeeAdminPanel = isSuperAdmin();
  const canSeeOperationalMenu = !hasRole(ROLES.MOTORISTA) || hasRole(ROLES.ADMIN, ROLES.MANAGER, ROLES.OPERADOR);

  const handleStopImpersonate = async () => {
    try {
      const { data } = await api.post<{ token: string }>('/admin/impersonate/stop');
      login(data.token);
      navigate('/admin');
    } catch {
      logout();
      navigate('/login');
    }
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<RouteEntity[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(getInitialSidebarOpen);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 769px)').matches);
  const notifCount = 0;

  useEffect(() => {
    // On mobile the sidebar is a drawer that should close after navigating;
    // on desktop it's a persistent collapse state the user controls, so leave it be.
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
    setSearchTerm('');
  }, [location.pathname, isDesktop]);

  useEffect(() => {
    if (isDesktop) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, isSidebarOpen ? '1' : '0');
    }
  }, [isSidebarOpen, isDesktop]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateCounts = () => {
    api.get<RouteEntity[]>('/routes').then((r) => setRoutes(r.data)).catch(() => {});
    api.get<Vehicle[]>('/vehicles').then((r) => setVehicles(r.data)).catch(() => {});
  };

  useEffect(() => {
    updateCounts();
    window.addEventListener('otimizarota-state-changed', updateCounts);
    return () => window.removeEventListener('otimizarota-state-changed', updateCounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routeCount = routes.length;
  const vehCount = vehicles.length;

  const searchResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return { routes: [] as RouteEntity[], vehicles: [] as Vehicle[] };
    const matchedRoutes = routes
      .filter((r) =>
        r.name.toLowerCase().includes(term) ||
        (r.vehicle?.driverName || '').toLowerCase().includes(term) ||
        (r.vehicle?.name || '').toLowerCase().includes(term) ||
        (r.vehicle?.plate || '').toLowerCase().includes(term)
      )
      .slice(0, 5);
    const matchedVehicles = vehicles
      .filter((v) =>
        v.name.toLowerCase().includes(term) ||
        v.plate.toLowerCase().includes(term) ||
        (v.driverName || '').toLowerCase().includes(term)
      )
      .slice(0, 5);
    return { routes: matchedRoutes, vehicles: matchedVehicles };
  }, [searchTerm, routes, vehicles]);

  const hasSearchResults = searchResults.routes.length > 0 || searchResults.vehicles.length > 0;
  const showSearchDropdown = isSearchFocused && searchTerm.trim().length > 0;
  const goToRoute = (id: string) => {
    navigate(`/rotas/${id}`);
    setSearchTerm('');
    setIsSearchFocused(false);
  };

  const goToVehicles = () => {
    navigate('/veiculos');
    setSearchTerm('');
    setIsSearchFocused(false);
  };

  const searchItems = [
    ...searchResults.routes.map((route) => ({ select: () => goToRoute(route.id) })),
    ...searchResults.vehicles.map(() => ({ select: goToVehicles }))
  ];

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsSearchFocused(false);
      setActiveSearchIndex(-1);
      return;
    }
    if (searchItems.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSearchIndex((index) => Math.min(index + 1, searchItems.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSearchIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeSearchIndex >= 0 && activeSearchIndex < searchItems.length) {
      event.preventDefault();
      searchItems[activeSearchIndex].select();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentSection = getBreadcrumb(location.pathname);
  const userName = me?.name || '...';
  const userInitials = me ? me.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '··';
  const companyName = me?.company?.name || '...';
  const userRole = me?.role || '';
  const primaryColor = me?.company?.primaryColor || '#082129';
  const secondaryColor = me?.company?.secondaryColor || '#7cd3c6';
  const logoUrl = me?.company?.logoUrl;

  return (
    <div className={`app ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      <aside
        className="sidebar"
        style={
          {
            '--sidebar-bg': primaryColor,
            '--sidebar-accent': secondaryColor
          } as React.CSSProperties
        }
      >
        <div className="brand" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          <div
            className="brand-mark"
            style={{
              background: logoUrl ? 'rgba(255, 255, 255, 0.08)' : `linear-gradient(135deg, ${secondaryColor}, #0f4c5c)`,
              overflow: 'hidden'
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="10" r="3" />
                <path d="M12 2a8 8 0 0 0-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z" />
              </svg>
            )}
          </div>
          <div className="brand-name">
            {companyName !== '...' ? companyName : <>Otimiza<span>Rota</span></>}
          </div>
        </div>

        <div className="nav-section">Operação</div>
        <nav className="nav">
          <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Dashboard" aria-label="Dashboard">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span className="nav-label">Dashboard</span>
          </NavLink>

          <NavLink to="/rotas" end className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Rotas" aria-label="Rotas">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="19" r="3"></circle>
              <circle cx="18" cy="5" r="3"></circle>
              <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-9a3.5 3.5 0 0 1 0-7H12"></path>
            </svg>
            <span className="nav-label">Rotas</span>
            <span className="num">{routeCount}</span>
          </NavLink>

          <NavLink to="/rotas/nova" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Nova rota" aria-label="Nova rota">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            <span className="nav-label">Nova rota</span>
          </NavLink>

          <NavLink to="/frota/nova" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Otimizar frota" aria-label="Otimizar frota">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle>
              <circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>
            <span className="nav-label">Otimizar frota</span>
            <span className="num" style={{ background: 'rgba(231,111,81,0.15)', color: 'var(--amber)' }}>VRP</span>
          </NavLink>
        </nav>

        <div className="nav-section">Cadastros</div>
        <nav className="nav">
          <NavLink to="/veiculos" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Veículos" aria-label="Veículos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            <span className="nav-label">Veículos</span>
            <span className="num">{vehCount}</span>
          </NavLink>

          <NavLink to="/depositos" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Depósitos" aria-label="Depósitos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span className="nav-label">Depósitos</span>
          </NavLink>

          <NavLink to="/lancamentos" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Lançamentos" aria-label="Lançamentos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 3h16v18H4z"></path>
              <path d="M8 7h8M8 11h8M8 15h4"></path>
            </svg>
            <span className="nav-label">Lançamentos</span>
          </NavLink>
        </nav>

        <div className="nav-section">Gestão</div>
        <nav className="nav">
          <NavLink to="/relatorios" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Relatórios" aria-label="Relatórios">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            <span className="nav-label">Relatórios</span>
          </NavLink>

          <NavLink to="/custos" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Custos & Rentabilidade" aria-label="Custos & Rentabilidade">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            <span className="nav-label">Custos & Rentabilidade</span>
            <span className="num" style={{ background: 'rgba(42,157,143,0.15)', color: 'var(--teal-500)' }}>BI</span>
          </NavLink>

        </nav>

        {(canManageUsers || canSeeAdminPanel) && (
          <>
            <div className="nav-section">Administração</div>
            <nav className="nav">
              {canManageUsers && (
                <NavLink to="/usuarios" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Usuários" aria-label="Usuários">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span className="nav-label">Usuários</span>
                </NavLink>
              )}
              {canSeeAdminPanel && (
                <NavLink to="/admin" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Painel do SaaS" aria-label="Painel do SaaS">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18"></path>
                    <path d="M5 21V7l7-4 7 4v14"></path>
                    <path d="M9 21V12h6v9"></path>
                  </svg>
                  <span className="nav-label">Painel do SaaS</span>
                </NavLink>
              )}
            </nav>
          </>
        )}

        <div className="nav-section">Sistema</div>
        <nav className="nav">
          <NavLink to="/whatsapp" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Rastreamento" aria-label="Rastreamento">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
            <span className="nav-label">Rastreamento</span>
            <span className="num" style={{ background: 'rgba(37,211,102,0.15)', color: '#25d366' }}>WA</span>
          </NavLink>

          <NavLink to="/configuracoes" className={({ isActive }) => (isActive ? 'active' : '')} data-tooltip="Configurações" aria-label="Configurações">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            <span className="nav-label">Configurações</span>
          </NavLink>
        </nav>

        <div className="side-foot">
          <div className="side-user" onClick={handleLogout} title="Clique para sair">
            <div className="avatar">{userInitials}</div>
            <div className="who">
              <div className="name">{userName}</div>
              <div className="role">{userRole} · {companyName}</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', opacity: 0.6 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </div>
        </div>
      </aside>

      <main className="main">
        {isImpersonating() && (
          <div
            role="status"
            style={{
              background: '#7c2d12',
              color: '#fff',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13
            }}
          >
            <strong>Modo suporte:</strong>
            <span>
              você está agindo como <strong>{me?.email}</strong> ({companyName}). Origem: {me?.impersonatedBy?.email}.
            </span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleStopImpersonate}
              style={{ marginLeft: 'auto', color: '#fff', background: 'transparent', borderColor: 'rgba(255,255,255,.5)' }}
            >
              Voltar ao meu usuário
            </button>
          </div>
        )}
        <div className="topbar">
          <button
            className="icon-btn sidebar-toggle-btn"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label={
              isDesktop
                ? (isSidebarOpen ? 'Expandir menu' : 'Recolher menu')
                : (isSidebarOpen ? 'Fechar menu' : 'Abrir menu')
            }
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div className="crumbs">
            <span>OtimizaRota</span>
            <span className="sep">/</span>
            <span className="cur">{currentSection}</span>
          </div>

          <div className="topbar-search" ref={searchBoxRef}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <input
              type="text"
              placeholder="Buscar rotas, veículos, motoristas..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setActiveSearchIndex(-1);
              }}
              onFocus={() => setIsSearchFocused(true)}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-expanded={showSearchDropdown}
              aria-controls="global-search-results"
              aria-activedescendant={activeSearchIndex >= 0 ? `global-search-option-${activeSearchIndex}` : undefined}
            />

            {showSearchDropdown && (
              <div id="global-search-results" className="search-dropdown" role="listbox">
                {!hasSearchResults && (
                  <div className="search-empty">Nenhum resultado para "{searchTerm}"</div>
                )}

                {searchResults.routes.length > 0 && (
                  <div className="search-group">
                    <div className="search-group-label">Rotas</div>
                    {searchResults.routes.map((r, index) => (
                      <div id={`global-search-option-${index}`} key={r.id} className={`search-item ${activeSearchIndex === index ? 'keyboard-active' : ''}`} role="option" aria-selected={activeSearchIndex === index} onClick={() => goToRoute(r.id)}>
                        <span className="search-item-title">{r.name}</span>
                        <span className="search-item-sub">
                          {r.vehicle?.driverName || '—'} · {r.vehicle?.name || 'Veículo'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.vehicles.length > 0 && (
                  <div className="search-group">
                    <div className="search-group-label">Veículos & Motoristas</div>
                    {searchResults.vehicles.map((v, index) => {
                      const searchIndex = searchResults.routes.length + index;
                      return (
                      <div id={`global-search-option-${searchIndex}`} key={v.id} className={`search-item ${activeSearchIndex === searchIndex ? 'keyboard-active' : ''}`} role="option" aria-selected={activeSearchIndex === searchIndex} onClick={goToVehicles}>
                        <span className="search-item-title">{v.name}</span>
                        <span className="search-item-sub">{v.driverName || '—'} · {v.plate}</span>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="topbar-right">
            <button className="icon-btn" onClick={() => navigate('/whatsapp')} title="Notificações WhatsApp">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 0 0-12 0c0 7 3 9 3 9h6s3-2 3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              {notifCount > 0 && <span className="badge">{notifCount}</span>}
            </button>

            <button className="icon-btn" onClick={() => navigate('/configuracoes')} title="Ajuda & Configurações">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </button>

            <div className="top-user" onClick={() => navigate('/configuracoes')} style={{ cursor: 'pointer' }}>
              <span className="name">{companyName}</span>
              <div className="avatar">{userInitials}</div>
            </div>
          </div>
        </div>

        <div className="page">{children}</div>
      </main>
    </div>
  );
}
